import manifestJson from "../../../data/manifest.json";
import topicsJson from "../../../data/topics.json";
import dependenciesJson from "../../../data/dependencies.json";
import capabilityAnchorsJson from "../../../data/capability_anchors.json";
import standardsJson from "../../../data/standards.json";

export type GradeBand = "G1" | "G2" | "G3" | "G4" | "G5" | "G6";
export type ModelLayer = "diagnostic" | "implementation_evidence";
export type DependencyStrength = "hard" | "soft";

export type DomainId =
  | "number-algebra"
  | "geometry"
  | "statistics-probability"
  | "integrated-practice";

export type Topic = {
  topicId: string;
  capabilityAnchor: string;
  standards: string[];
  modelLayer: ModelLayer;
  gradeBand: GradeBand;
  domainIds: DomainId[];
  displayName: string;
  displayDescription: string;
};

export type Dependency = {
  topicId: string;
  prerequisiteId: string;
  strength: DependencyStrength;
  reason: string;
  evidenceLayer: "candidate";
};

export type TopicDependency = {
  edge: Dependency;
  topic: Topic;
};

export type CapabilityAnchor = {
  id: string;
  name: string;
  standards: string[];
};

export type Standard = {
  id: string;
  title: string;
};

export type Dataset = {
  datasetVersion: string;
  status: string;
  candidate: boolean;
  topics: Topic[];
  dependencies: Dependency[];
  capabilityAnchors: CapabilityAnchor[];
  standards: Standard[];
  counts: {
    topics: number;
    diagnosticTopics: number;
    dependencies: number;
    capabilityAnchors: number;
    standards: number;
  };
};

type RawTopic = {
  id: string;
  name: string;
  description: string;
  standards: string[];
  capabilityAnchor: string;
  modelLayer: ModelLayer;
};

type RawDependency = {
  topicId: string;
  prerequisiteId: string;
  strength: DependencyStrength;
  reason: string;
};

type RawManifest = {
  datasetVersion: string;
  status: string;
  counts: {
    topics: number;
    dependencies: number;
    standards: number;
    capabilityAnchors: number;
  };
};

type RawCapabilityAnchor = {
  id: string;
  name: string;
  standards: string[];
};

type RawStandard = {
  id: string;
  title: string;
};

export const DOMAIN_LABELS: Record<DomainId, string> = {
  "number-algebra": "数与代数",
  geometry: "图形与几何",
  "statistics-probability": "统计与概率",
  "integrated-practice": "综合与实践"
};

const manifest = manifestJson as RawManifest;
const rawTopics = topicsJson as RawTopic[];
const rawDependencies = dependenciesJson as RawDependency[];
const rawCapabilityAnchors = capabilityAnchorsJson as RawCapabilityAnchor[];
const rawStandards = standardsJson as RawStandard[];

const gradePattern = /^cn_math_(g[1-6])_/;
const domainIds = Object.keys(DOMAIN_LABELS) as DomainId[];

const stableGradeFromTopicId = (topicId: string): GradeBand => {
  const match = topicId.match(gradePattern);
  if (!match) {
    throw new Error(`topicId does not encode a stable grade band: ${topicId}`);
  }
  return match[1].toUpperCase() as GradeBand;
};

const stableDomainsFromStandards = (standards: string[]): DomainId[] => {
  const matched = domainIds.filter((domainId) =>
    standards.some((standardId) => standardId.includes(`/primary/${domainId}/`))
  );
  return matched.length > 0 ? matched : ["number-algebra"];
};

const topics: Topic[] = rawTopics.map((topic) => ({
  topicId: topic.id,
  capabilityAnchor: topic.capabilityAnchor,
  standards: topic.standards,
  modelLayer: topic.modelLayer,
  gradeBand: stableGradeFromTopicId(topic.id),
  domainIds: stableDomainsFromStandards(topic.standards),
  displayName: topic.name,
  displayDescription: topic.description
}));

const topicById = new Map(topics.map((topic) => [topic.topicId, topic]));

const dependencies: Dependency[] = rawDependencies.map((edge) => ({
  topicId: edge.topicId,
  prerequisiteId: edge.prerequisiteId,
  strength: edge.strength,
  reason: edge.reason,
  evidenceLayer: "candidate"
}));

const isDiagnosticTopicId = (topicId: string): boolean =>
  topicById.get(topicId)?.modelLayer === "diagnostic";

const diagnosticOnlyEdges = dependencies.filter(
  (edge) => isDiagnosticTopicId(edge.topicId) && isDiagnosticTopicId(edge.prerequisiteId)
);

const dataset: Dataset = {
  datasetVersion: manifest.datasetVersion,
  status: manifest.status,
  candidate: manifest.datasetVersion.includes("candidate") || manifest.status.includes("candidate"),
  topics,
  dependencies: diagnosticOnlyEdges,
  capabilityAnchors: rawCapabilityAnchors.map((anchor) => ({
    id: anchor.id,
    name: anchor.name,
    standards: anchor.standards
  })),
  standards: rawStandards.map((standard) => ({
    id: standard.id,
    title: standard.title
  })),
  counts: {
    topics: manifest.counts.topics,
    diagnosticTopics: topics.filter((topic) => topic.modelLayer === "diagnostic").length,
    dependencies: diagnosticOnlyEdges.length,
    capabilityAnchors: manifest.counts.capabilityAnchors,
    standards: manifest.counts.standards
  }
};

export function loadDataset(): Dataset {
  return dataset;
}

export function diagnosticTopics(gradeBand?: GradeBand): Topic[] {
  return dataset.topics
    .filter((topic) => topic.modelLayer === "diagnostic")
    .filter((topic) => !gradeBand || topic.gradeBand === gradeBand);
}

export function prerequisites(topicId: string, recursive = false): TopicDependency[] {
  return collectTopicDependencies(topicId, "prerequisites", recursive);
}

export function unlocks(topicId: string, recursive = false): TopicDependency[] {
  return collectTopicDependencies(topicId, "unlocks", recursive);
}

export function topicsByAnchor(capabilityAnchorId: string): Topic[] {
  return diagnosticTopics().filter((topic) => topic.capabilityAnchor === capabilityAnchorId);
}

export function topicsByStandard(standardId: string): Topic[] {
  return diagnosticTopics().filter((topic) => topic.standards.includes(standardId));
}

function collectTopicDependencies(
  topicId: string,
  direction: "prerequisites" | "unlocks",
  recursive: boolean
): TopicDependency[] {
  const seenEdges = new Set<string>();
  const seenTopics = new Set<string>([topicId]);
  const results: TopicDependency[] = [];
  const queue = [topicId];

  while (queue.length > 0) {
    const currentTopicId = queue.shift()!;
    const matchingEdges = dataset.dependencies.filter((edge) =>
      direction === "prerequisites"
        ? edge.topicId === currentTopicId
        : edge.prerequisiteId === currentTopicId
    );

    for (const edge of matchingEdges) {
      const nextTopicId = direction === "prerequisites" ? edge.prerequisiteId : edge.topicId;
      const nextTopic = topicById.get(nextTopicId);
      const edgeKey = `${edge.topicId}->${edge.prerequisiteId}`;

      if (!nextTopic || nextTopic.modelLayer !== "diagnostic" || seenEdges.has(edgeKey)) {
        continue;
      }

      seenEdges.add(edgeKey);
      results.push({ edge, topic: nextTopic });

      if (recursive && !seenTopics.has(nextTopicId)) {
        seenTopics.add(nextTopicId);
        queue.push(nextTopicId);
      }
    }
  }

  return results;
}
